<template>
	<div id="cl-ar-chart-modal-widget">
		<h3 class="module-title">
			Annual Returns
			<slot name="superscript1" />
		</h3>
		<ReadMoreLink />
		<div>
			<p class="chart-description">
				{{ shareClassText }} tracks the total returns since the {{ fundLabel }}'s inception
				date ({{ componentInceptionDate }}) through {{ componentMaxDate }}. {{ fundLabelCapitalized }} returns and, if available,
				index returns are for calendar years except for the inception year ({{ componentInceptionYear }}), which may not be a full
				calendar year. In cases where the index was launched after the {{ fundLabel }} inception, the index returns are shown in
				calendar years.
			</p>
		</div>
		<div class="chart-legend">
			<div>
				<span class="square bg-brand-primary" />
				<span>{{ primaryLabel }}</span>
			</div>
			<div v-if="secondaryLabel">
				<span class="square bg-brand-secondary" />
				<span>{{ secondaryLabel }}</span>
			</div>
			<div>
				<span class="square bg-neutral-primary" />
				<span class="index">Index</span>
				<div class="dropdown">
					<select>
						<option>{{ componentIndexName }}</option>
					</select>
				</div>
				<slot name="superscript2" />
				<slot name="annualReturnsIndexId" />
			</div>
		</div>
		<div>
			<div class="main-chart-container">
				<div id="annual-returns-hover" />
				<div v-if="isSvg" ref="mainChart" v-html="componentReturnsChart" />
				<img v-else ref="mainChart" :src="componentReturnsChart" />
			</div>
			<div class="nav-container" @customdrag="dragHandler($event)">
				<chart-panel ref="leftPanel" @customdrag="dragHandler($event)" />
				<chart-slider
					ref="slider1"
					sliderId="slider1"
					@update-drag="updateDragValues"
					@update-chart="updateChart"
				/>
				<chart-slider
					ref="slider2"
					sliderId="slider2"
					class="right-slider"
					@update-drag="updateDragValues"
					@update-chart="updateChart"
				/>
				<chart-panel ref="rightPanel" class="right-panel" @customdrag="dragHandler($event)" />
				<div v-if="isSvg" ref="navChart" v-html="componentReturnsChartNav" />
				<img v-else ref="navChart" class="nav-chart" :src="componentReturnsChartNav" />
			</div>
		</div>
	</div>
</template>

<script>
import ChartPanel from './ChartPanel';
import ChartSlider from './ChartSlider';
import * as tooltips from '_assets_/js/tooltips.js';
import ReadMoreLink from '_components_/Shared/ReadMoreLink';
import * as utils from '../../../shared/utils/utils';
export default {
	name: 'ReturnsLargeChart',
	components: {
		'chart-panel': ChartPanel,
		'chart-slider': ChartSlider,
		ReadMoreLink
	},
	props: [
		'returnsChart',
		'returnsChartData',
		'returnsChartNav',
		'returnsChartNavData',
		'inceptionDate',
		'maxDate',
		'fundName',
		'indexName',
		'widgetData'
		//fundSymbol and shareClass both come from the globally registered links mixin
	],
	data() {
		return {
			chartWidth: 0,
			modalWidth: 0,
			drag1: 0,
			drag2: 0,
			navHeight: 0,
			navLeft: 0,
			scale: 0,
			year1: 0,
			year2: 0,
			yearMap: [],
			chartData: {},
			navData: []
		};
	},
	computed: {
		componentData() {
			return this.widgetData || {};
		},
		componentShareClass() {
			return this.shareClass || this.componentData.shareClass || '';
		},
		componentFundName() {
			return this.fundName || this.componentData.fundName || '';
		},
		componentFundSymbol() {
			return this.fundSymbol || this.componentData.fundSymbol || '';
		},
		componentReturnsChart() {
			return this.returnsChart || this.componentData.returnsChart || '';
		},
		componentReturnsChartNav() {
			return (
				this.returnsChartNav || this.componentData.returnsChartNav || ''
			);
		},
		componentInceptionDate() {
			// returns dates like "Thursday, July 31, 1952"
			const inceptionDate =
				this.inceptionDate || this.componentData.inceptionDate;
			return inceptionDate
				? this.$format.fullDate(inceptionDate)
				: '--';
		},
		componentInceptionYear() {
			const date = this.inceptionDate || this.componentData.inceptionDate;
			return date ? new Date(date).getFullYear() : '--';
		},
		componentMaxDate() {
			// returns dates like "September 30, 2019"
			const maxDate =
				this.maxDate || this.componentData.returnsAsOf || '';
			return maxDate ? this.$format.longDate(maxDate) : '--';
		},
		componentIndexName() {
			return (
				this.indexName ||
				(this.componentData.IndexData &&
					this.componentData.IndexData.FundName) ||
				'--'
			);
		},
		fundLabel() {
			//by default this is 'fund' but you could pass it something else like strategy
			return this.componentData.fundLabel || 'fund';
		},
		fundLabelCapitalized() {
			//the fund label when it appears at the start of the sentence with the first letter capitalized
			//this is a bit lengthy to all fit inside of a template so I made a new property
			const label = this.fundLabel;
			return label.charAt(0).toUpperCase() + label.slice(1);
		},
		shareClassText() {
			let text = 'This chart';
			if (this.componentShareClass) {
				text = `For class ${this.componentShareClass} Shares, this chart`;
			}
			return text;
		},
		primaryLabel() {
			let label = this.componentData.primaryLabel || '--';
			if (this.componentFundName && this.componentFundSymbol) {
				label = `${this.componentFundName} (${this.componentFundSymbol})`;
			}
			return label;
		},
		secondaryLabel() {
			return this.componentData.secondaryLabel || '';
		},
		primaryHoverLabel() {
			let label = this.componentData.primaryHoverLabel || '--';
			if (this.componentFundSymbol) {
				label = this.componentFundSymbol;
			}
			return label;
		},
		secondaryHoverLabel() {
			return this.componentData.secondaryHoverLabel || '';
		},
		isSvg() {
			return this.componentData.returnsSvg || false;
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.setModalWidth();
			this.initChart();
			this.watchForChartChanges();
			this.setNavLeft();
			this.setChartScale();
			this.adjustPanels();
			this.adjustSliders();
			if (this.isSvg) {
				this.getMetaDataSvg();
				this.attachHoversSvg();
				this.setScaleSvg();
				this.createYearMapSvg();
			} else {
				this.parseData();
				this.createYearMap();
				this.attachHovers();
			}
		});
	},
	updated() {
		if (!this.isSvg) {
			this.parseData();
			this.attachHovers();
		}
	},
	methods: {
		watchForChartChanges() {
			const mainChart = this.$refs.mainChart;
			const navChart = this.$refs.navChart;
			const config = { childList: true };

			const mainChartObserver = new MutationObserver(
				this.handleMainChartLoad
			);
			mainChartObserver.observe(mainChart, config);

			const navChartObserver = new MutationObserver(
				this.handleNavChartLoad
			);
			navChartObserver.observe(navChart, config);
		},
		handleMainChartLoad() {
			if (this.isSvg) {
				this.scaleMainChartSvg();
				this.getMetaDataSvg();
				this.attachHoversSvg();
			}
		},
		handleNavChartLoad() {
			if (this.isSvg) {
				this.setNavLeft();
				this.scaleNavChartSvg();
				this.createYearMapSvg();
			}
		},
		initChart() {
			//this calls the initial chart after the tab is changed
			this.$parent.$parent.$emit('request-chart', {
				startYear: 0,
				endYear: 0
			});
		},
		updateDragValues(dragData) {
			if (dragData.sliderId && dragData.sliderId == 'slider1') {
				this.drag1 = dragData.dragPosition - this.navLeft;
			} else {
				this.drag2 = dragData.dragPosition - this.navLeft;
			}
			if (this.drag1 < 0) {
				this.drag1 = 0;
			}
			if (this.drag2 < 0) {
				this.drag2 = 0;
			}
			if (this.drag1 > this.chartWidth) {
				this.drag1 = this.chartWidth;
			}
			if (this.drag2 > this.chartWidth) {
				this.drag2 = this.chartWidth;
			}
			this.adjustSliders();
			this.adjustPanels();
		},
		updateChart() {
			this.getYear();
			let startYear = 0;
			let endYear = 0;

			if (this.year1 < this.year2) {
				startYear = this.year1;
				endYear = this.year2;
			} else {
				startYear = this.year2;
				endYear = this.year1;
			}

			//the non svg version does not include the end year in the returned chart
			//For the desired result we need to increase the end year by 1
			//adjust later if necessary - maybe make this a param if more control is needed?
			if (!this.isSvg) {
				endYear++;
			}

			this.$parent.$parent.$emit('request-chart', { startYear, endYear });
		},
		setChartScale() {
			const chart = this.$refs.mainChart;
			this.chartWidth = chart.clientWidth;
			this.drag2 = this.chartWidth - 2;

			this.scale = this.chartWidth / 970;
			this.navHeight = 126 * this.scale;
		},
		setModalWidth() {
			const contentContainer = document.getElementById(
				'cl-chart-modal-widget'
			);
			const style =
				contentContainer.currentStyle ||
				window.getComputedStyle(contentContainer);
			const margin =
				parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
			this.modalWidth = contentContainer.clientWidth - margin;
		},
		setNavLeft() {
			const navChart = this.$refs.navChart;

			if (navChart.x) {
				//Google chrome has an x value on the chart that has the correct position
				this.navLeft = navChart.x;
			} else {
				//If the x value is not available you can find it by traversing the DOM and adding offset values
				//Since the element is not fully loaded on the page this is the only solution that worked for me
				let currentElement = navChart;
				while (currentElement.offsetParent) {
					currentElement = currentElement.offsetParent;
					if (currentElement.offsetLeft) {
						this.navLeft += currentElement.offsetLeft;
					}
				}
			}
		},
		adjustPanels() {
			let leftPanelPosition = this.drag1;
			let rightPanelPosition = this.drag2;

			if (this.drag1 > this.drag2) {
				//Slider 1 is now the 'right' slider
				leftPanelPosition = this.drag2;
				rightPanelPosition = this.drag1;
			}

			const rightPanel = this.$refs.rightPanel.$el;
			const leftPanel = this.$refs.leftPanel.$el;

			rightPanel.style.left = `${rightPanelPosition}px`;
			rightPanel.style.width = `${this.chartWidth -
				rightPanelPosition}px`;
			leftPanel.style.width = `${leftPanelPosition}px`;

			//Sets the panel height on initial call
			if (!rightPanel.style.height) {
				rightPanel.style.height = `${this.navHeight}px`;
				leftPanel.style.height = `${this.navHeight}px`;
			}
		},
		adjustSliders() {
			const slider1 = this.$refs.slider1.$el;
			const slider2 = this.$refs.slider2.$el;

			slider1.style.left = `${this.drag1}px`;
			slider2.style.left = `${this.drag2}px`;

			if (!slider1.style.height) {
				const height = this.navHeight;
				slider1.style.height = `${height - 3}px`;
				slider2.style.height = `${height - 3}px`;

				//Sets the height of the touch targets
				slider1.children[0].style.height = `${height}px`;
				slider2.children[0].style.height = `${height}px`;
			}
		},
		parseData() {
			this.chartData = this.jsonSafeParse(this.returnsChartData);
			this.navData = eval(this.returnsChartNavData); // eslint-disable-line
		},
		createYearMap() {
			if (this.chartData[0] && this.navData) {
				let year = this.chartData[0].Year;

				this.yearMap = [];
				for (let i = 0; i < this.navData.length; i++) {
					const pos = this.navData[i] * this.scale;
					this.yearMap.push({ pos, year });
					year++;
				}
			}
		},
		getYear() {
			this.year1 = 0;
			this.year2 = 0;

			for (let i = 0; i < this.yearMap.length; i++) {
				const position = this.yearMap[i].pos;
				const yearSelector = i == 0 ? i : i - 1;
				if (this.year1 == 0 && this.drag1 < position) {
					this.year1 = this.yearMap[yearSelector].year;
				}
				if (this.year2 == 0 && this.drag2 < position) {
					this.year2 = this.yearMap[yearSelector].year;
				}
				if (this.year1 != 0 && this.year2 != 0) {
					break;
				}
			}
			const lastYear = this.yearMap[this.yearMap.length - 1];
			if (this.drag1 > lastYear.pos) {
				this.year1 = lastYear.year;
			}
			if (this.drag2 > lastYear.pos) {
				this.year2 = lastYear.year;
			}
		},
		detachHovers() {
			const hoverContainer = document.getElementById(
				'annual-returns-hover'
			);
			if (hoverContainer) {
				hoverContainer.innerHTML = '';
			}
		},
		attachHovers() {
			const data = this.chartData;
			this.detachHovers();

			const hoverContainer = document.getElementById(
				'annual-returns-hover'
			);
			for (const value in data) {
				if (
					Object.prototype.hasOwnProperty.call(data, value) &&
					data[value].Height > 0
				) {
					const dataPoint = data[value];
					const chartAnchor = this.createChartAnchor(
						dataPoint,
						this.scale
					);
					const toolTip = tooltips.createTooltip(
						(dataPoint.X + dataPoint.Width / 2) * this.scale,
						dataPoint.Y * this.scale,
						this.createToolTipBody(dataPoint)
					);
					toolTip.classList.add('toolTip');

					hoverContainer.appendChild(chartAnchor);
					hoverContainer.appendChild(toolTip);

					chartAnchor.addEventListener(
						'mouseenter',
						this.showToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'mouseleave',
						this.hideToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'touchstart',
						this.toggleToolTip.bind(null, toolTip),
						false
					);
				}
			}
		},
		createChartAnchor(dataPoint, scale) {
			const chartAnchor = document.createElement('a');
			chartAnchor.setAttribute(
				'style',
				`top:${dataPoint.Y * scale}px; left:${dataPoint.X *
					scale}px; position: absolute; height: ${dataPoint.Height *
					scale}px; width: ${dataPoint.Width * scale}px;`
			);

			return chartAnchor;
		},
		createToolTipBody(dataPoint) {
			const body = document.createElement('div');
			body.style.padding = '5px';
			body.style.textAlign = 'center';

			const value = document.createElement('div');
			value.style.fontSize = '18px';

			const name = document.createElement('div');
			const nameText = dataPoint.Symbol;
			const year = document.createElement('div');

			value.appendChild(document.createTextNode(`${dataPoint.Value}%`));
			name.appendChild(document.createTextNode(nameText));
			year.appendChild(
				document.createTextNode(`${dataPoint.Year} Returns`)
			);

			body.appendChild(value);
			body.appendChild(name);
			body.appendChild(year);

			return body;
		},
		showToolTip(toolTip) {
			this.hideAllToolTips();
			toolTip.style.display = 'block';
			const leftModify = toolTip.offsetWidth / 2;
			const arrowModify = leftModify - 20;
			const topModify = toolTip.offsetHeight + 30;
			const leftValue = toolTip.dataset.left - leftModify;

			const children = toolTip.childNodes;
			children[1].style.left = `${arrowModify}px`;
			children[2].style.left = `${arrowModify + 1}px`;

			toolTip.style.top = `${toolTip.dataset.top - topModify}px`;
			toolTip.style.left = `${leftValue}px`;
			toolTip.style.fontFamily = '"Avenir Next LT W01 Demi", Arial';
			toolTip.style.whiteSpace = 'nowrap';

			if (leftValue + toolTip.offsetWidth > this.modalWidth) {
				const newLeftVal = this.modalWidth - toolTip.offsetWidth;
				const arrowLeft = parseInt(children[1].style.left, 10);
				const adjustArrow = leftValue - newLeftVal;

				toolTip.style.left = `${newLeftVal}px`;
				children[1].style.left = `${arrowLeft + adjustArrow}px`;
				children[2].style.left = `${arrowLeft + adjustArrow + 1}px`;
			}

			if (leftValue < 0) {
				const adjustArrow = 0 - leftValue;
				const arrowLeft = parseInt(children[1].style.left, 10);
				toolTip.style.left = 0;
				children[1].style.left = `${arrowLeft - adjustArrow}px`;
				children[2].style.left = `${arrowLeft - adjustArrow + 1}px`;
			}
		},
		hideToolTip(toolTip) {
			toolTip.style.display = 'none';
		},
		toggleToolTip(toolTip) {
			if (toolTip.style.display === 'none') {
				this.showToolTip(toolTip);
			} else {
				this.hideToolTip(toolTip);
			}
		},
		hideAllToolTips() {
			const tips = document.getElementsByClassName('toolTip');
			for (let i = 0; i < tips.length; i++) {
				tips[i].style.display = 'none';
			}
		},
		setScaleSvg() {
			this.scaleMainChartSvg();
			this.scaleNavChartSvg();
		},
		scaleMainChartSvg() {
			this.scale = this.modalWidth / 970;

			const mainChart =
				this.$refs.mainChart && this.$refs.mainChart.children[0];
			if (mainChart) {
				const height = mainChart.height.baseVal.value;
				mainChart.width.baseVal.value = this.modalWidth;
				mainChart.height.baseVal.value = height * this.scale;
			}
		},
		scaleNavChartSvg() {
			this.scale = this.modalWidth / 970;

			const navChart =
				this.$refs.navChart && this.$refs.navChart.children[0];
			if (navChart) {
				const height = navChart.height.baseVal.value;
				navChart.width.baseVal.value = this.modalWidth;
				navChart.height.baseVal.value = height * this.scale;
			}
		},
		attachHoversSvg() {
			const hoverContainer = document.getElementById(
				'annual-returns-hover'
			);
			this.detachHovers();
			const bars = this.$refs.mainChart.getElementsByClassName('bar');

			for (let i = 0; i < bars.length; i++) {
				const toolTip = this.createBarTooltip(bars[i]);
				bars[i].addEventListener('mouseenter', this.openBarHoverSvg);
				bars[i].addEventListener('mouseleave', this.closeBarHoverSvg);
				bars[i].addEventListener('touchstart', this.toggleBarHoverSvg);
				hoverContainer.appendChild(toolTip);
			}
		},
		createYearMapSvg() {
			const navBars = this.$refs.navChart.getElementsByClassName('bar');
			const yearMap = [];
			let year = 0;
			let pos = 0;

			for (let i = 0; i < navBars.length; i++) {
				const bar = navBars[i];
				const json = bar.getAttribute('data-json');
				const data = this.jsonSafeParse(json);

				let newPos = data.X * this.scale;
				let dist = 0;
				//When transitioning years we need to take the whitespace into account
				//First calculate the amount of space, then split it between the previous and new year entry
				if (year && year != data.Year) {
					const endBar = pos + data.Width;
					dist = newPos - endBar;
					newPos = newPos - dist / 2;

					yearMap.pop();
					pos = pos + dist / 2;
					yearMap.push({ pos, year });
				}
				pos = newPos;
				year = data.Year;
				yearMap.push({ pos, year });
			}
			this.yearMap = yearMap;
		},
		createBarTooltip(bar) {
			const dataString = bar.getAttribute('data-json');
			const data = this.jsonSafeParse(dataString);
			const secondaryLabels = [
				this.primaryHoverLabel,
				this.secondaryHoverLabel,
				this.componentIndexName
			];
			const labelsMap = this.secondaryHoverLabel
				? secondaryLabels
				: [this.primaryHoverLabel, this.componentIndexName];

			data.Symbol = labelsMap[data.BarIndex];
			const toolTipBody = this.createToolTipBody(data);
			const toolTipX = data.X + bar.width.baseVal.value / 2;
			const toolTip = tooltips.createTooltip(
				toolTipX * this.scale,
				data.Y * this.scale,
				toolTipBody
			);
			toolTip.classList.add('toolTip');
			toolTip.classList.add(bar.id);
			return toolTip;
		},
		getBarToolTip(e) {
			const hoverContainer = document.getElementById(
				'annual-returns-hover'
			);
			const target = e.currentTarget;
			const id = target.id;
			const toolTip = hoverContainer.getElementsByClassName(id);
			return toolTip;
		},
		openBarHoverSvg(e) {
			const toolTip = this.getBarToolTip(e);
			this.showToolTip(toolTip[0]);
		},
		closeBarHoverSvg(e) {
			const toolTip = this.getBarToolTip(e);
			this.hideToolTip(toolTip[0]);
		},
		toggleBarHoverSvg(e) {
			const toolTip = this.getBarToolTip(e);
			this.toggleToolTip(toolTip[0]);
		},
		getMetaDataSvg() {
			const metaElement = document.getElementById('meta-data');
			if (metaElement) {
				const jsonString = metaElement.getAttribute('data-json');
				const data = this.jsonSafeParse(jsonString);
				this.componentIndexName = data.IndexName || '--';
				this.componentInceptionDate = data.InceptionDate || '--';
				this.componentInceptionYear = data.InceptionYear || '--';
				this.componentMaxDate = data.MaxDate || '--';
			}
		},
		jsonSafeParse(jsonString) {
			let jsonVal = {};
			try {
				jsonVal = JSON.parse(jsonString);
			} catch (e) {
				//
			}
			return jsonVal;
		}
	}
};
</script>

<style scoped lang="less">
#cl-ar-chart-modal-widget {
	.read-disclosures-link {
		margin-bottom: 20px;
		a {
			cursor: pointer;
			font-family: Avenir Next LT W01 Demi, Arial, sans-serif;
			font-weight: 700;
		}
	}
	.chart-description {
		color: #333;
		font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
		font-size: 14px;
		font-weight: 400;
		margin-bottom: 20px;
	}
	.chart-legend {
		color: #666;
		font-family: 'AvenirNextLTW01-Regular', Arial, sans-serif;
		margin-bottom: 10px;
		.dropdown {
			margin-right: 5px;
		}
	}
	img {
		width: 100%;
	}
	.nav-container {
		margin-top: 15px;
		position: relative;
	}
	#annual-returns-hover {
		position: relative;
	}
	@media (max-width: 768px) {
		.chart-legend {
			.dropdown {
				display: inline;
				select {
					width: 70%;
				}
			}
		}
	}
}
</style>
